import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export function PlateBookPanel({ onChanged }) {
  const [book, setBook] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get("/avstone/platebook").then((r) => setBook(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const send = async (payload) => {
    setBusy(true);
    try {
      const r = await api.post("/avstone/platebook/import", payload, { timeout: 120000 });
      toast.success(`${r.data.added} added · ${r.data.updated} updated · ${r.data.total} plates on file`);
      if (r.data.skipped?.length) toast.warning(`Skipped (not a plate): ${r.data.skipped.join(", ")}`);
      setText(""); load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const upload = async (file) => {
    if (!file) return;
    const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(",")[1]); fr.readAsDataURL(file); });
    send({ filename: file.name, file_b64: b64 });
  };
  const del = async (id) => { await api.delete(`/avstone/platebook/${id}`); load(); };
  const shown = book.filter((b) => !q || `${b.plate} ${b.label}`.toUpperCase().includes(q.toUpperCase()));
  return (
    <div className="border border-white/10 p-4 space-y-3" data-testid="platebook-panel">
      <div>
        <h2 className="font-display font-black text-base">Customer &amp; carrier plates ({book.length})</h2>
        <p className="text-xs text-[#71717A]">One per line: <span className="font-mono-data">PLATE, Customer name</span>. Or upload an Excel/CSV with the plate in column A and the name in column B. Every truck visit and plate read then shows who the vehicle belongs to.</p>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder={"P123456, ABC Countertops\nXY98765, Stone Freight LLC"}
        className="w-full bg-black border border-white/20 p-2 font-mono-data text-sm" data-testid="platebook-text" />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => send({ text })} disabled={busy || !text.trim()} className="px-4 py-1.5 bg-white text-black text-xs font-bold disabled:opacity-40" data-testid="platebook-save-btn">{busy ? "Saving…" : "Save list"}</button>
        <label className={`px-4 py-1.5 border border-white/30 text-xs font-bold cursor-pointer hover:bg-white/10 transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="platebook-file-btn">
          Upload Excel / CSV
          <input type="file" accept=".csv,.txt,.xlsx,.xlsm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="platebook-file-input" />
        </label>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plate or customer" className="ml-auto bg-black border border-white/20 px-2 py-1.5 text-sm w-56" data-testid="platebook-search" />
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2 max-h-80 overflow-y-auto" data-testid="platebook-list">
        {shown.map((b) => (
          <div key={b.id} className="flex items-center gap-3 border border-white/10 p-2" data-testid={`platebook-item-${b.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-mono-data font-black tracking-widest">{b.plate}</div>
              <div className="text-[11px] text-[#A1A1AA] truncate">{b.label || "—"}</div>
            </div>
            <button onClick={() => del(b.id)} className="text-[#FF3B30] text-xs px-2" data-testid={`platebook-delete-${b.id}`}>Remove</button>
          </div>
        ))}
        {!book.length && <div className="text-xs text-[#71717A]" data-testid="platebook-empty">No customer plates yet.</div>}
      </div>
    </div>
  );
}
